import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AddressUtil } from 'src/app/helpers/utils/address.util';
import { BiomeUtil } from 'src/app/helpers/utils/biome.util';
import { CpfCnpjValidator } from 'src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator';
import { formatCPFOrCNPJ, removeNonDigitsCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { normalizeExternalLink } from 'src/app/helpers/utils/link.util';
import { Locations } from 'src/app/shared/interfaces/psa-project-register';
import { AppModalService } from 'src/app/shared/services/app-modal.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { ModalConfirmationComponent } from '../modal-confirmation/modal-confirmation.component';
import { ProjectTable } from '../project-table/project-table.component';

@Component({
  standalone: false,
  selector: 'app-psa-add-others-owners',
  templateUrl: './psa-add-others-owners.component.html',
  styleUrls: ['./psa-add-others-owners.component.scss'],
})
export class AddOthersOwnersComponent extends ProjectTable<Locations> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 5;

  @Input() formRegister!: FormGroup;
  @Output() ownersChanged = new EventEmitter<Locations[]>();

  locationsAdded: Locations[] = [];
  locationsSubscription?: Subscription;
  private pendingPropertyEdit = false;

  private biomeUtil = new BiomeUtil();
  private addressUtil = new AddressUtil();

  constructor(
    private modalService: AppModalService,
    private readonly toastService: AppToastService,
  ) {
    super();
    this.pagination.page = 1;
    this.pagination.size = AddOthersOwnersComponent.PAGE_SIZE;
  }

  ngOnInit(): void {
    const initialLocations = this.formRegister.get('locations')?.value;
    if (initialLocations && Array.isArray(initialLocations)) {
      this.syncLocations(initialLocations);
    }

    this.locationsSubscription = this.formRegister.get('locations')?.valueChanges.subscribe((value: Locations[]) => {
      if (value && Array.isArray(value)) {
        this.syncLocations(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.locationsSubscription?.unsubscribe();
  }

  get paginatedLocations(): Locations[] {
    const page = this.pagination.page ?? 1;
    const size = this.pagination.size ?? AddOthersOwnersComponent.PAGE_SIZE;
    const start = (page - 1) * size;
    return this.locationsAdded.slice(start, start + size);
  }

  get currentPage(): number {
    return this.pagination.page ?? 1;
  }

  trackByLocation(_index: number, location: Locations): string {
    return `${location.landOwnerCpfCnpj ?? ''}-${location.landOwnerFullName ?? ''}-${location.registrationRecord ?? ''}`;
  }

  formatDocument(value?: string): string {
    return formatCPFOrCNPJ(value ?? '');
  }

  formatBiome(value?: string): string {
    if (!value) {
      return '';
    }

    const biome = this.biomeUtil.biomes.find(item => item.inputId === value);
    return biome?.label ?? value.replace(/_/g, ' ');
  }

  getCoordinatesLabel(location: Locations): string {
    if (location.coordinates) {
      return location.coordinates;
    }

    return `Latitude: ${location.latitude ?? ''} | Longitude: ${location.longitude ?? ''}`;
  }

  addLocations(): boolean {
    const owner = this.buildOwnerFromForm();

    const validate = this.validateLocations(owner);
    if (validate !== null) {
      this.markPropertyFieldsTouched();
      this.treatOwnerAddError(validate);
      return false;
    }

    const locationExists = this.locationsAdded.some(item => this.matchesLocation(item, owner));

    if (locationExists) {
      this.treatOwnerAddError('Este CAR já foi incluído na lista.');
      return false;
    }

    this.locationsAdded.push(owner);
    this.updateLocationsList();
    this.formRegister.get('locations')?.setValue([...this.locationsAdded]);
    this.formRegister.get('locations')?.markAsTouched();
    this.formRegister.get('locations')?.updateValueAndValidity();
    this.ownersChanged.emit([...this.locationsAdded]);
    this.showPropertyLinkedToast(owner);
    this.clearInputs();
    return true;
  }

  validateDisabledButton(): boolean {
    return this.validateLocations(this.buildOwnerFromForm()) !== null;
  }

  deleteLocation(location: Locations): void {
    const index = this.findLocationIndex(location);

    if (index === -1) {
      return;
    }

    const [removed] = this.locationsAdded.splice(index, 1);
    this.restoreFormFromLocation(removed);
    this.pendingPropertyEdit = true;
    this.updateLocationsList();
    this.syncLocationsFormControl();
    this.ownersChanged.emit([...this.locationsAdded]);
    this.toastService.show({
      variant: 'warning',
      label: 'Propriedade desvinculada!',
      description: this.buildPropertyToastDescription(removed),
    });
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.onPageChange(page);
  }

  private syncLocations(locations: Locations[]): void {
    locations.forEach(location => {
      const lat = location.latitude ?? '';
      const lon = location.longitude ?? '';
      location.coordinates = `Latitude: ${lat} | Longitude: ${lon}`;
    });
    this.locationsAdded = locations;
    this.updateLocationsList();
  }

  private updateLocationsList(): void {
    this.itens = [...this.locationsAdded];
    this.totalItens = this.locationsAdded.length;
    this.handleTotalPages({ pageSize: this.pagination.size ?? AddOthersOwnersComponent.PAGE_SIZE });

    if ((this.pagination.page ?? 1) > this.totalPages) {
      this.pagination.page = this.totalPages || 1;
    }
  }

  private syncLocationsFormControl(): void {
    this.formRegister.get('locations')?.setValue([...this.locationsAdded], { emitEvent: false });

    if (this.locationsAdded.length === 0) {
      this.formRegister.get('locations')?.markAsUntouched();
    }
  }

  private matchesLocation(left: Locations, right: Locations): boolean {
    const leftRecord = String(left.registrationRecord ?? '').trim();
    const rightRecord = String(right.registrationRecord ?? '').trim();
    const leftLink = normalizeExternalLink(left.registrationRecordLink);
    const rightLink = normalizeExternalLink(right.registrationRecordLink);

    return !!leftRecord && leftRecord === rightRecord && !!leftLink && leftLink === rightLink;
  }

  private buildOwnerFromForm(): Locations {
    const owner: Locations = {
      landOwnerFullName: this.getStringFromControl('landOwnerFullName'),
      landOwnerCpfCnpj: this.getStringFromControl('landOwnerCpf'),
      latitude: this.getStringFromControl('lat'),
      longitude: this.getStringFromControl('long'),
      country: this.getStringFromControl('country') || 'Brasil',
      state: this.getUf(),
      municipality: this.getCityName(),
      totalHectares: this.getNumberFromControl('totalHectares'),
      carbonGenerationHectares: this.getNumberFromControl('carbonGenerationHectares'),
      availableHectares: this.getNumberFromControl('availableHectares'),
      biome: this.getStringFromControl('biome'),
      registrationRecord: this.getStringFromControl('registrationRecord'),
      registrationRecordLink: normalizeExternalLink(this.getStringFromControl('onlineStatementLink')),
    };

    owner.coordinates = this.getCoordinatesLabel(owner);
    return owner;
  }

  private getStringFromControl(controlName: string): string {
    const value = this.formRegister.get(controlName)?.value;
    if (value == null) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim();
    }

    return String(value?.label ?? value?.inputId ?? value?.data ?? '').trim();
  }

  private getUf(): string {
    const value = this.formRegister.get('uf')?.value;
    if (value == null) {
      return '';
    }

    return (typeof value === 'string' ? value : (value.inputId ?? value.label ?? '')).toString().trim().toUpperCase();
  }

  private getCityName(): string {
    const value = this.formRegister.get('city')?.value;
    if (value == null) {
      return '';
    }

    return (typeof value === 'string' ? value : (value.label ?? value.data ?? value.inputId ?? '')).toString().trim();
  }

  private getNumberFromControl(controlName: string): number {
    const control = this.formRegister.get(controlName);
    const value = control?.value;
    const raw = typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : value;
    const parsed = Number(raw);
    return isNaN(parsed) ? 0 : parsed;
  }

  private findLocationIndex(location: Locations): number {
    const matchedIndex = this.locationsAdded.findIndex(item => this.matchesLocation(item, location));
    if (matchedIndex !== -1) {
      return matchedIndex;
    }

    return this.locationsAdded.indexOf(location);
  }

  private restoreFormFromLocation(location: Locations): void {
    const totalHectares = location.totalHectares ?? '';

    if (Number(totalHectares) > 0) {
      this.formRegister.get('carbonGenerationHectares')?.enable({ emitEvent: false });
    }

    this.formRegister.patchValue({
      landOwnerFullName: location.landOwnerFullName ?? '',
      landOwnerCpf: location.landOwnerCpfCnpj ?? '',
      lat: location.latitude ?? '',
      long: location.longitude ?? '',
      country: location.country || 'Brasil',
      totalHectares,
      carbonGenerationHectares: location.carbonGenerationHectares ?? '',
      availableHectares: location.availableHectares ?? '',
      biome: location.biome ?? '',
      registrationRecord: location.registrationRecord ?? '',
      onlineStatementLink: location.registrationRecordLink ?? '',
    });

    // Município primeiro (sem emit); UF com emit dispara reload no combo e reaplica o município.
    const municipality = String(location.municipality ?? '').trim();
    const municipalityValue = municipality
      ? { inputId: municipality, label: municipality, data: municipality }
      : null;
    const cityCtrl = this.formRegister.get('city');
    cityCtrl?.enable({ emitEvent: false });
    cityCtrl?.setValue(municipalityValue, { emitEvent: false });

    const stateAbbr = String(location.state ?? '').trim().toUpperCase();
    const ufOption = this.addressUtil.estadosBrasil.find(
      item => String(item.inputId).toUpperCase() === stateAbbr,
    );
    this.formRegister.get('uf')?.setValue(
      ufOption
        ? { ...ufOption }
        : (stateAbbr ? { inputId: stateAbbr, label: stateAbbr, data: stateAbbr } : null),
    );

    // Safety net: se o combo zerar o município no reload, reaplica após as options do select.
    if (municipalityValue) {
      setTimeout(() => {
        const currentCity = this.formRegister.get('city')?.value;
        const currentLabel = typeof currentCity === 'string'
          ? currentCity
          : String(currentCity?.label ?? currentCity?.data ?? '').trim();
        if (!currentLabel) {
          this.formRegister.get('city')?.enable({ emitEvent: false });
          this.formRegister.get('city')?.setValue({ ...municipalityValue }, { emitEvent: false });
        }
      }, 300);
    }
  }

  private clearInputs(): void {
    this.pendingPropertyEdit = false;
    this.formRegister.get('landOwnerFullName')?.setValue('');
    this.formRegister.get('landOwnerCpf')?.setValue('');
    this.formRegister.get('lat')?.setValue('');
    this.formRegister.get('long')?.setValue('');
    this.formRegister.get('country')?.setValue('Brasil');
    this.formRegister.get('uf')?.setValue(null);
    this.formRegister.get('city')?.setValue(null);
    this.formRegister.get('totalHectares')?.setValue('');
    this.formRegister.get('carbonGenerationHectares')?.setValue('');
    this.formRegister.get('availableHectares')?.setValue('');
    this.formRegister.get('biome')?.setValue('');
    this.formRegister.get('registrationRecord')?.setValue('');
    this.formRegister.get('onlineStatementLink')?.setValue('');
    this.formRegister.get('carbonGenerationHectares')?.disable({ emitEvent: false });
  }

  private showPropertyLinkedToast(owner: Locations): void {
    if (this.pendingPropertyEdit) {
      this.toastService.show({
        variant: 'info',
        label: 'Confirmada alteração em Propriedade vinculada!',
        description: this.buildPropertyToastDescription(owner),
      });
      return;
    }

    this.toastService.show({
      variant: 'success',
      label: 'Propriedade vinculada com sucesso!',
      description: this.buildPropertyToastDescription(owner),
    });
  }

  private buildPropertyToastDescription(owner: Locations): string {
    const name = owner.landOwnerFullName?.trim() || '';
    return `${name} CPF/CNPJ ${this.formatDocument(owner.landOwnerCpfCnpj)}`;
  }

  private validateLocations(location: Locations): string | null {
    const requiredTextFields: Array<{ key: keyof Locations; label: string }> = [
      { key: 'landOwnerFullName', label: 'Nome/Razão Social do proprietário' },
      { key: 'landOwnerCpfCnpj', label: 'CPF/CNPJ do proprietário' },
      { key: 'latitude', label: 'Latitude' },
      { key: 'longitude', label: 'Longitude' },
      { key: 'country', label: 'País' },
      { key: 'state', label: 'UF' },
      { key: 'municipality', label: 'Município' },
      { key: 'biome', label: 'Bioma' },
      { key: 'registrationRecord', label: 'Registro de inscrição (CAR)' },
      { key: 'registrationRecordLink', label: 'Link do demonstrativo online (CAR)' },
    ];

    for (const field of requiredTextFields) {
      const value = location[field.key];
      if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
        return `Preencha o campo "${field.label}" antes de incluir a propriedade.`;
      }
    }

    const documentDigits = removeNonDigitsCNPJ(String(location.landOwnerCpfCnpj ?? ''));
    if (
      documentDigits.length !== CpfCnpjValidator.cpfLength &&
      documentDigits.length !== CpfCnpjValidator.cnpjLength
    ) {
      return 'O CPF/CNPJ do proprietário deve conter 11 ou 14 dígitos.';
    }

    if ((location.totalHectares ?? 0) <= 0) {
      return 'Informe os hectares totais da propriedade (valor maior que zero).';
    }

    if ((location.carbonGenerationHectares ?? 0) <= 0) {
      return 'Informe os hectares de geração de carbono (valor maior que zero).';
    }

    if (location.carbonGenerationHectares! > location.totalHectares!) {
      return 'Os hectares de geração de carbono não podem ser maiores que os hectares totais.';
    }

    return null;
  }

  private markPropertyFieldsTouched(): void {
    const fields = [
      'landOwnerFullName',
      'landOwnerCpf',
      'lat',
      'long',
      'uf',
      'city',
      'totalHectares',
      'carbonGenerationHectares',
      'biome',
      'registrationRecord',
      'onlineStatementLink',
      'locations',
    ];

    fields.forEach(fieldName => this.formRegister.get(fieldName)?.markAsTouched());
  }

  treatOwnerAddError(msg: string): void {
    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'error',
      modalType: 'info-ok',
      title: 'Atenção',
      content: msg,
      closeButton: true,
    });
  }
}
